/**
 * Skills Matrix Service
 * 
 * API calls and types for the Skills Matrix feature.
 * Scores shortlisted CVs against TOR criteria in a side-by-side compliance matrix.
 */

import axios, { AxiosError } from 'axios';
import ExcelJS from 'exceljs';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ─── Types ───────────────────────────────────────────────────────────────────

/** A single criterion column in the matrix */
export interface MatrixCriterion {
    id: string;
    label: string;
    category: 'thematic' | 'donor' | 'geographic' | 'methods' | 'languages' | 'education' | 'experience' | 'custom';
    search_terms: string[];
    enabled: boolean;
}

/** Score + evidence for one CV × one criterion cell */
export interface CriterionScore {
    score: number;        // 0–3
    evidence: string;     // 1–2 sentence supporting evidence
    hit_count?: number;   // keyword hits (text-search fallback)
}

/** One row in the matrix: a single candidate */
export interface CandidateResult {
    cv_id: number;
    associate_name: string;
    filename: string;
    folder_name?: string;
    source_location?: string;
    scores: Record<string, CriterionScore>;  // keyed by criterion id
    total_score: number;
    max_possible: number;
    percentage: number;
    overall_narrative?: string;
}

/** Full matrix response from the API */
export interface SkillsMatrixResult {
    criteria: MatrixCriterion[];
    results: CandidateResult[];
    generation_time_ms: number;
    summary?: MatrixSummary;
}

/** Quick-glance insights above the table */
export interface MatrixSummary {
    best_fit: { name: string; score: string; percentage: number } | null;
    hardest_criterion: { label: string; avg_score: number } | null;
    gap_notes: string[];
}

/** Minimal CV info for candidate selection */
export interface CandidateOption {
    cv_id: number;
    filename: string;
    associate_name: string;
    folder_name?: string;
    source_location?: string;
}

// ─── API Calls ───────────────────────────────────────────────────────────────

/**
 * Generate the skills matrix by scoring CVs against criteria.
 */
export async function generateSkillsMatrix(
    criteria: MatrixCriterion[],
    cvIds: number[],
    cvSource: 'associate' | 'iod_staff' = 'associate',
    topK: number = 20
): Promise<SkillsMatrixResult> {
    try {
        const response = await axios.post<SkillsMatrixResult>(
            `${API_URL}/generate-skills-matrix`,
            {
                criteria: criteria.filter(c => c.enabled),
                cv_ids: cvIds,
                cv_source: cvSource,
                top_k: topK
            },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 180000 // 3 minutes — scoring many CVs takes time
            }
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError<{ error?: string }>;
            if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ERR_CONNECTION_CLOSED') {
                throw new Error('Skills Matrix service is not available.');
            }
            if (axiosError.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            const serverMessage = axiosError.response?.data?.error;
            if (serverMessage) {
                throw new Error(serverMessage);
            }
        }
        throw new Error('Failed to generate Skills Matrix. Please try again.');
    }
}

/**
 * Search CVs by name for the candidate selector.
 * Reuses the existing search-cvs-by-name endpoint.
 */
export async function searchCandidatesByName(
    query: string,
    cvSource: 'associate' | 'iod_staff' = 'associate'
): Promise<CandidateOption[]> {
    try {
        const response = await axios.post<{ cvs: CandidateOption[] }>(
            `${API_URL}/search-cvs-by-name`,
            { query, cv_source: cvSource },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 15000
            }
        );
        return response.data.cvs || [];
    } catch {
        console.warn('Failed to search candidates by name');
        return [];
    }
}

// ─── TOR-to-Criteria Transformer ─────────────────────────────────────────────

/**
 * Convert extracted TOR requirements (ConsultantRequirementsV2 shape)
 * into a flat array of MatrixCriterion for the scoring table.
 */
export function torRequirementsToCriteria(requirements: any): MatrixCriterion[] {
    const criteria: MatrixCriterion[] = [];
    let idCounter = 1;

    const makeId = () => `c_${idCounter++}`;

    // Methods / Methodologies
    const methods = requirements.methods_methodologies?.items ||
        requirements.research_evaluation_skills?.required?.concat(requirements.research_evaluation_skills?.preferred || []) || [];
    for (const item of methods) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'methods',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Technical domain expertise
    const techDomain = requirements.technical_domain_expertise?.items || [];
    for (const item of techDomain) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'thematic',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Sectors
    const sectors = requirements.sector_experience?.items ||
        (requirements.sector_experience?.required || []).concat(requirements.sector_experience?.preferred || []);
    for (const item of sectors) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'thematic',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Geographic — regions + countries
    const geoRegions = requirements.geographic_experience?.regions ||
        (requirements.geographic_experience?.required_regions || []).concat(requirements.geographic_experience?.preferred_regions || []);
    const geoCountries = requirements.geographic_experience?.countries ||
        (requirements.geographic_experience?.required_countries || []).concat(requirements.geographic_experience?.preferred_countries || []);

    for (const item of [...geoRegions, ...geoCountries]) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'geographic',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Languages
    const languages = requirements.language_requirements?.items ||
        (requirements.language_requirements?.required || []).concat(requirements.language_requirements?.preferred || []);
    for (const item of languages) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'languages',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Education
    const eduLevel = requirements.education_requirements?.minimum_level;
    const eduFields = requirements.education_requirements?.fields ||
        (requirements.education_requirements?.required_fields || []).concat(requirements.education_requirements?.preferred_fields || []);
    if (eduLevel) {
        const fieldTexts = eduFields.map((f: any) => typeof f === 'string' ? f : f.text).filter(Boolean);
        const label = fieldTexts.length > 0
            ? `${eduLevel} in ${fieldTexts.join(' / ')}`
            : eduLevel;
        criteria.push({
            id: makeId(), label, category: 'education',
            search_terms: [eduLevel.toLowerCase(), ...fieldTexts.map((t: string) => t.toLowerCase())],
            enabled: true
        });
    }

    // Experience years
    const minYears = requirements.experience_requirements?.minimum_years;
    if (minYears) {
        criteria.push({
            id: makeId(), label: `${minYears}+ years experience`, category: 'experience',
            search_terms: [`${minYears} years`, 'years of experience', 'years experience'],
            enabled: true
        });
    }

    // Technical tools
    const tools = requirements.technical_tools_software?.items ||
        requirements.analytical_tools?.required?.concat(requirements.analytical_tools?.preferred || []) || [];
    for (const item of tools) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'methods',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Frameworks
    const frameworks = requirements.frameworks_standards?.items ||
        requirements.frameworks_methodologies?.required?.concat(requirements.frameworks_methodologies?.preferred || []) || [];
    for (const item of frameworks) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'methods',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Donor experience
    const donors = requirements.donor_experience?.items ||
        (requirements.donor_experience?.required || []).concat(requirements.donor_experience?.preferred || []);
    for (const item of donors) {
        const text = typeof item === 'string' ? item : item.text;
        if (text) {
            criteria.push({
                id: makeId(), label: text, category: 'donor',
                search_terms: [text.toLowerCase()], enabled: true
            });
        }
    }

    // Auto-inject EU Experience if not already present
    const hasEU = criteria.some(c =>
        c.category === 'donor' &&
        (c.label.toLowerCase().includes('eu') || c.label.toLowerCase().includes('european'))
    );
    if (!hasEU) {
        criteria.push({
            id: makeId(),
            label: 'EU / European Commission Experience',
            category: 'donor',
            search_terms: ['eu', 'european union', 'europeaid', 'european commission', 'dg intpa', 'dg near', 'edf', 'horizon', 'eidhr', 'eu-funded'],
            enabled: true
        });
    }

    return criteria;
}

// ─── Excel Export ─────────────────────────────────────────────────────────────

/** Score → fill color (ARGB for ExcelJS) */
const EXCEL_SCORE_BG: Record<number, string> = {
    0: 'FFFCE4EC', // light red
    1: 'FFFFF3E0', // light amber
    2: 'FFE8F5E9', // light green
    3: 'FFC8E6C9', // darker green
};

/** Score → font color (ARGB) */
const EXCEL_SCORE_FG: Record<number, string> = {
    0: 'FFC62828', // dark red
    1: 'FFE65100', // dark amber
    2: 'FF2E7D32', // dark green
    3: 'FF1B5E20', // darker green
};

/** Score labels for legend */
const EXCEL_SCORE_LABELS: Record<number, string> = {
    0: 'No evidence',
    1: 'Mentioned / Limited',
    2: 'Demonstrated',
    3: 'Extensive / Expert',
};

/** Category → header color (ARGB) */
const CATEGORY_HEADER_COLORS: Record<string, string> = {
    thematic: 'FF7C4DFF',
    donor: 'FFFF6D00',
    geographic: 'FF00BFA5',
    methods: 'FF2979FF',
    languages: 'FF00E676',
    education: 'FFF50057',
    experience: 'FFFF9100',
    custom: 'FF78909C',
};

const CATEGORY_LABELS: Record<string, string> = {
    thematic: 'Thematic / Sector',
    donor: 'Donor Experience',
    geographic: 'Geographic',
    methods: 'Methods / Tools',
    languages: 'Languages',
    education: 'Education',
    experience: 'Experience',
    custom: 'Custom',
};

/**
 * Export the skills matrix to an Excel workbook and trigger download.
 * Mirrors the on-screen table formatting: category headers, color-coded scores,
 * borders, frozen panes, summary panel, and score legend.
 */
export async function exportMatrixToExcel(
    matrixResult: SkillsMatrixResult,
    torTitle?: string
): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'IOD Toolbox — Skills Matrix';
    workbook.created = new Date();

    const enabledCriteria = matrixResult.criteria.filter(c => c.enabled !== false);
    const totalCols = enabledCriteria.length + 3; // name + criteria + total + %

    const thinBorder: Partial<ExcelJS.Borders> = {
        top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
    };

    // ── Sheet 1: Matrix ──────────────────────────────────────────────────────
    const ws = workbook.addWorksheet('Skills Matrix');

    let currentRow = 1;

    // Title row
    if (torTitle) {
        const titleRow = ws.addRow([`Skills Matrix: ${torTitle}`]);
        titleRow.font = { bold: true, size: 14, color: { argb: 'FF1A237E' } };
        ws.mergeCells(currentRow, 1, currentRow, totalCols);
        currentRow++;
        const dateRow = ws.addRow([`Generated: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`]);
        dateRow.font = { size: 10, italic: true, color: { argb: 'FF757575' } };
        ws.mergeCells(currentRow, 1, currentRow, totalCols);
        currentRow++;
        ws.addRow([]);
        currentRow++;
    }

    // ── Category group header row ────────────────────────────────────────────
    const catHeaderData: string[] = [''];
    const catHeaderColors: string[] = [''];
    for (const c of enabledCriteria) {
        catHeaderData.push(CATEGORY_LABELS[c.category] || c.category);
        catHeaderColors.push(CATEGORY_HEADER_COLORS[c.category] || 'FF78909C');
    }
    catHeaderData.push('', '');
    catHeaderColors.push('', '');

    const catRow = ws.addRow(catHeaderData);
    catRow.height = 18;
    catRow.eachCell((cell, colNum) => {
        if (colNum > 1 && colNum <= enabledCriteria.length + 1) {
            const color = catHeaderColors[colNum - 1];
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
            cell.font = { bold: true, size: 8, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF424242' } };
        }
        cell.border = thinBorder;
    });
    currentRow++;

    // ── Criterion label header row ───────────────────────────────────────────
    const headers = ['Candidate', ...enabledCriteria.map(c => c.label), 'Total', '%'];
    const headerRow = ws.addRow(headers);
    headerRow.height = 36;
    headerRow.eachCell((cell, colNum) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A2E' } };
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = thinBorder;
        if (colNum === 1) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
    });
    const headerRowNum = currentRow;
    currentRow++;

    // ── Data rows ────────────────────────────────────────────────────────────
    for (const candidate of matrixResult.results) {
        const name = candidate.associate_name || candidate.filename.replace(/\.pdf$/i, '');
        const rowData = [
            name,
            ...enabledCriteria.map(c => candidate.scores[c.id]?.score ?? 0),
            candidate.total_score,
            candidate.percentage / 100
        ];
        const dataRow = ws.addRow(rowData);
        dataRow.height = 22;

        // Name cell
        const nameCell = dataRow.getCell(1);
        nameCell.font = { bold: true, size: 10 };
        nameCell.alignment = { vertical: 'middle' };
        nameCell.border = thinBorder;

        // Score cells
        enabledCriteria.forEach((c, i) => {
            const cell = dataRow.getCell(i + 2);
            const score = candidate.scores[c.id]?.score ?? 0;
            const bg = EXCEL_SCORE_BG[score] || 'FFFFFFFF';
            const fg = EXCEL_SCORE_FG[score] || 'FF000000';
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
            cell.font = { bold: true, size: 11, color: { argb: fg } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = thinBorder;
        });

        // Total cell
        const totalCell = dataRow.getCell(enabledCriteria.length + 2);
        totalCell.font = { bold: true, size: 11 };
        totalCell.alignment = { horizontal: 'center', vertical: 'middle' };
        totalCell.border = thinBorder;

        // Percentage cell
        const pctCell = dataRow.getCell(enabledCriteria.length + 3);
        pctCell.numFmt = '0%';
        pctCell.font = { bold: true, size: 11 };
        pctCell.alignment = { horizontal: 'center', vertical: 'middle' };
        pctCell.border = thinBorder;
        // Conditional color for percentage
        const pct = candidate.percentage;
        if (pct >= 60) {
            pctCell.font = { bold: true, size: 11, color: { argb: 'FF1B5E20' } };
            pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC8E6C9' } };
        } else if (pct >= 35) {
            pctCell.font = { bold: true, size: 11, color: { argb: 'FFE65100' } };
            pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } };
        } else {
            pctCell.font = { bold: true, size: 11, color: { argb: 'FFC62828' } };
            pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE4EC' } };
        }
    }

    // ── Column widths ────────────────────────────────────────────────────────
    ws.getColumn(1).width = 28;
    for (let i = 2; i <= enabledCriteria.length + 1; i++) {
        ws.getColumn(i).width = 16;
    }
    ws.getColumn(enabledCriteria.length + 2).width = 10;
    ws.getColumn(enabledCriteria.length + 3).width = 10;

    // ── Freeze panes (candidate name column + header rows) ───────────────────
    ws.views = [{ state: 'frozen', xSplit: 1, ySplit: headerRowNum, showGridLines: false }];

    // ── Score Legend (below data) ────────────────────────────────────────────
    ws.addRow([]);
    const legendTitle = ws.addRow(['Score Legend']);
    legendTitle.font = { bold: true, size: 11 };
    for (let s = 0; s <= 3; s++) {
        const legendRow = ws.addRow([`${s} — ${EXCEL_SCORE_LABELS[s]}`]);
        const cell = legendRow.getCell(1);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_SCORE_BG[s] } };
        cell.font = { bold: true, color: { argb: EXCEL_SCORE_FG[s] } };
    }

    // ── Summary row (below legend) ──────────────────────────────────────────
    if (matrixResult.summary) {
        ws.addRow([]);
        const summaryTitle = ws.addRow(['Summary']);
        summaryTitle.font = { bold: true, size: 12 };
        if (matrixResult.summary.best_fit) {
            ws.addRow([`Best Overall Fit: ${matrixResult.summary.best_fit.name} (${matrixResult.summary.best_fit.score}, ${matrixResult.summary.best_fit.percentage}%)`]);
        }
        if (matrixResult.summary.hardest_criterion) {
            ws.addRow([`Hardest to Fill: ${matrixResult.summary.hardest_criterion.label} (avg ${matrixResult.summary.hardest_criterion.avg_score}/3)`]);
        }
        if (matrixResult.summary.gap_notes?.length > 0) {
            for (const note of matrixResult.summary.gap_notes) {
                ws.addRow([note]);
            }
        }
    }

    // ── Sheet 2: Evidence ────────────────────────────────────────────────────
    const evidenceSheet = workbook.addWorksheet('Evidence');
    const evHeader = evidenceSheet.addRow(['Candidate', 'Criterion', 'Category', 'Score', 'Evidence']);
    evHeader.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A2E' } };
        cell.alignment = { horizontal: 'center' };
    });

    for (const candidate of matrixResult.results) {
        const name = candidate.associate_name || candidate.filename.replace(/\.pdf$/i, '');
        for (const c of enabledCriteria) {
            const s = candidate.scores[c.id];
            const score = s?.score ?? 0;
            const evRow = evidenceSheet.addRow([name, c.label, c.category, score, s?.evidence || 'No evidence found']);
            const scoreCell = evRow.getCell(4);
            scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_SCORE_BG[score] } };
            scoreCell.font = { bold: true, color: { argb: EXCEL_SCORE_FG[score] } };
            scoreCell.alignment = { horizontal: 'center' };
        }
    }

    evidenceSheet.getColumn(1).width = 25;
    evidenceSheet.getColumn(2).width = 30;
    evidenceSheet.getColumn(3).width = 15;
    evidenceSheet.getColumn(4).width = 8;
    evidenceSheet.getColumn(5).width = 80;
    evidenceSheet.views = [{ state: 'frozen', ySplit: 1 }];

    // ── Sheet 3: Criteria ────────────────────────────────────────────────────
    const criteriaSheet = workbook.addWorksheet('TOR Criteria');
    const crHeader = criteriaSheet.addRow(['Criterion', 'Category', 'Search Terms']);
    crHeader.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A2E' } };
    });

    for (const c of enabledCriteria) {
        const crRow = criteriaSheet.addRow([c.label, c.category, c.search_terms.join(', ')]);
        const catCell = crRow.getCell(2);
        const catColor = CATEGORY_HEADER_COLORS[c.category] || 'FF78909C';
        catCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: catColor } };
        catCell.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } };
    }

    criteriaSheet.getColumn(1).width = 35;
    criteriaSheet.getColumn(2).width = 18;
    criteriaSheet.getColumn(3).width = 60;
    criteriaSheet.views = [{ state: 'frozen', ySplit: 1 }];

    // ── Download ─────────────────────────────────────────────────────────────
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Skills_Matrix_${torTitle ? torTitle.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40) : 'Export'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}
